import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Pagination, PaginationItem, PaginationLink } from '@/components/ui-compat'
import { Button } from '@/components/ui/button'
import { Plus, Calendar, ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-react'

import useRouter from '../../../hooks/useRouter'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import * as Query from '../../../api/gql/query'
import MeetingCard, { MeetingCardData } from './MeetingCard'
import MeetingFilterBar, { MeetingsFiltersState } from './MeetingFilterBar'
import MeetingsListSkeleton from './MeetingsListSkeleton'
import MeetingModal from './MeetingModal'

const PAGE_SIZE = 25

const MeetingsList: React.FC = () => {
  const { t } = useTranslation()
  const { history, match } = useRouter()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const { canCreate, canEdit } = useModuleActions('MEETINGS', projectId)

  const [filters, setFilters] = useState<MeetingsFiltersState>({
    search: '',
    type: 'all',
    status: 'all',
  })
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const serverFilter = useMemo(() => {
    const f: any = { projectId }
    if (filters.type !== 'all') f.type = [filters.type]
    if (filters.status !== 'all') f.status = [filters.status]
    if (filters.search.trim()) f.search = filters.search.trim()
    return f
  }, [projectId, filters])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.type, filters.status, filters.search])

  const { data, loading, refetch } = useQuery(Query.MEETINGS_CONNECTION, {
    variables: { filter: serverFilter, page: currentPage, pageSize: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  })

  const connection = data?.meetingsConnection
  const pageInfo = connection?.pageInfo
  const totalCount: number = connection?.totalCount || 0
  const meetings: MeetingCardData[] = connection?.items || []

  const handleView = (m: MeetingCardData) => {
    history.push(`${match.url.replace(/\/$/, '')}/${m.id}`)
  }

  const handleEdit = (m: MeetingCardData) => {
    setEditingId(m.id)
    setModalOpen(true)
  }

  const handleNew = () => {
    setEditingId(null)
    setModalOpen(true)
  }

  const handleClose = (saved?: boolean) => {
    setModalOpen(false)
    setEditingId(null)
    if (saved) refetch()
  }

  if (loading && !data) return <MeetingsListSkeleton />

  return (
    <div>
      <div className="py-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="font-bold text-lg">{totalCount}</span>
          <span className="text-muted-foreground text-sm">{t('Common_total')}</span>
        </div>

        <MeetingFilterBar value={filters} onChange={setFilters} />

        <div className="ml-auto flex items-center gap-2">
          {canCreate && (
            <Button size="sm" className="h-8" onClick={handleNew}>
              <Plus className="h-4 w-4" />
              {t('Meetings_new_button')}
            </Button>
          )}
        </div>
      </div>

      {meetings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Calendar className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-sm mb-4">{t('Meetings_empty_hint')}</p>
          {canCreate && (
            <Button size="sm" onClick={handleNew}>
              <Plus className="h-4 w-4" />
              {t('Meetings_empty_cta')}
            </Button>
          )}
        </div>
      ) : (
        <div className="relative min-h-[200px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          <div className="space-y-2">
            {meetings.map((m) => (
              <MeetingCard
                key={m.id}
                meeting={m}
                onView={handleView}
                onEdit={canEdit ? handleEdit : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <div className="px-1 py-3 flex items-center justify-between">
          <div className="text-muted-foreground text-sm">
            {t('Meetings_showing', {
              from: (pageInfo.currentPage - 1) * pageInfo.pageSize + 1,
              to: Math.min(pageInfo.currentPage * pageInfo.pageSize, totalCount),
              total: totalCount,
            })}
          </div>
          <Pagination size="sm" className="mb-0">
            <PaginationItem disabled={!pageInfo.hasPreviousPage}>
              <PaginationLink
                previous
                onClick={() => setCurrentPage(currentPage - 1)}
                className={pageInfo.hasPreviousPage ? 'cursor-pointer' : 'cursor-not-allowed'}
              >
                <ChevronLeft className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>

            {pageInfo.currentPage > 2 && (
              <PaginationItem>
                <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
              </PaginationItem>
            )}

            {pageInfo.currentPage > 3 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}

            {pageInfo.currentPage > 1 && (
              <PaginationItem>
                <PaginationLink onClick={() => setCurrentPage(currentPage - 1)}>
                  {pageInfo.currentPage - 1}
                </PaginationLink>
              </PaginationItem>
            )}

            <PaginationItem active>
              <PaginationLink>{pageInfo.currentPage}</PaginationLink>
            </PaginationItem>

            {pageInfo.currentPage < pageInfo.totalPages && (
              <PaginationItem>
                <PaginationLink onClick={() => setCurrentPage(currentPage + 1)}>
                  {pageInfo.currentPage + 1}
                </PaginationLink>
              </PaginationItem>
            )}

            {pageInfo.currentPage < pageInfo.totalPages - 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}

            {pageInfo.currentPage < pageInfo.totalPages - 1 && (
              <PaginationItem>
                <PaginationLink onClick={() => setCurrentPage(pageInfo.totalPages)}>
                  {pageInfo.totalPages}
                </PaginationLink>
              </PaginationItem>
            )}

            <PaginationItem disabled={!pageInfo.hasNextPage}>
              <PaginationLink
                next
                onClick={() => setCurrentPage(currentPage + 1)}
                className={pageInfo.hasNextPage ? 'cursor-pointer' : 'cursor-not-allowed'}
              >
                <ChevronRight className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      )}

      {modalOpen && projectId && (
        <MeetingModal
          isOpen={modalOpen}
          onClose={handleClose}
          meetingId={editingId}
          projectId={projectId}
        />
      )}
    </div>
  )
}

export default MeetingsList
